import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/api";
import { toast } from "sonner";
import { devDebug } from "@/lib/devDebug";
import { Heart, MapPin, Info, ArrowRight, Filter, ListOrdered, Gift, BarChart3, Globe2, TrendingUp, ShieldCheck, Check, Bell, X, Mail } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All Properties" },
  { id: "residential", label: "Residential" },
  { id: "airbnb", label: "Airbnb Rentals" },
  { id: "commercial", label: "Commercial" },
  { id: "luxury", label: "Luxury Villas" },
  { id: "hospitality", label: "Hospitality" },
];

/** Centered "You're already on the waitlist" modal with one-click email reminder.
 *  Rendered via a portal in `Modal.js` so it always centers on the viewport. */
const WaitlistJoinedModal = ({ property, onClose }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const remind = async () => {
    setSending(true);
    try {
      await api.post("/properties/remind", { property_id: property.id });
      setSent(true);
      toast.success("Reminder set — we'll email you the moment allocation opens.");
    } catch (e) {
      toast.error("Couldn't set reminder. Try again in a moment.");
      devDebug("[remind] failed", e);
    } finally {
      setSending(false);
    }
  };

  const node = (
    <div
      data-testid="waitlist-joined-modal"
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm fade-in p-6"
      onClick={onClose}
    >
      <div
        className="onex-card max-w-md w-full p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="waitlist-joined-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
        >
          <X size={16} className="text-zinc-300" />
        </button>

        <div className="w-14 h-14 rounded-2xl bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
          <Check size={22} className="text-[#F59E0B]" />
        </div>
        <h3 className="text-[22px] font-display tracking-tight text-white mt-5 leading-tight">
          You{"\u2019"}re on the list ✓
        </h3>
        <p className="text-zinc-400 text-[14px] mt-2 leading-relaxed">
          You{"\u2019"}ve already joined the waitlist for <span className="text-white font-medium">{property.name}</span>.
          {" "}<span className="text-[#8CFF2E]">{property.waitlist_count}+ members</span> are queued for {property.spots_total} spots.
          Members get a 24-hour priority window before the public release.
        </p>

        <button
          onClick={remind}
          disabled={sending || sent}
          data-testid="waitlist-remind-btn"
          className={`mt-6 w-full ${sent ? "btn-ghost border-[#22C55E]/40 text-[#22C55E]" : "btn-gold"}`}
        >
          {sent
            ? <><Mail size={16} /> Reminder sent to your inbox</>
            : sending
              ? <><Bell size={16} /> Sending…</>
              : <><Bell size={16} /> Remind me via email</>}
        </button>
        <p className="text-[12px] text-zinc-500 mt-3 text-center">
          We{"\u2019"}ll only ping you when allocation opens. No spam. Promise.
        </p>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

const DubaiProperties = () => {
  const [category, setCategory] = useState("all");
  const [properties, setProperties] = useState([]);
  const [waitlistedProperty, setWaitlistedProperty] = useState(null);

  const load = useCallback((cat = category) => api.get(`/properties${cat && cat !== "all" ? `?category=${cat}` : ""}`).then(({ data }) => {
    setProperties(data.properties);
    // Log the "browse properties" milestone trigger silently — first property is enough.
    if (data.properties?.length) {
      api.post("/properties/view", { property_id: data.properties[0].id }).catch(() => {});
    }
  }), [category]);
  useEffect(() => { load(category); }, [category, load]);

  const join = async (p) => {
    // If already joined → show the centered "remind me" modal instead of re-posting.
    if (p.joined_waitlist) {
      setWaitlistedProperty(p);
      return;
    }
    try { await api.post("/properties/waitlist", { property_id: p.id }); toast.success(`Joined waitlist for ${p.name}`); load(category); }
    catch { toast.error("Could not join waitlist"); }
  };
  const save = async (p) => {
    try { const { data } = await api.post("/properties/save", { property_id: p.id }); toast.success(data.saved ? "Saved to favorites" : "Removed from favorites"); load(category); }
    catch (e) { devDebug("[properties] save failed", e); }
  };

  const savedCount = useMemo(() => properties.filter(p => p.saved).length, [properties]);

  return (
    <div data-testid="properties-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#1F3008] border border-[#8CFF2E]/30 flex items-center justify-center"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 22V8l6-4 6 4v14M9 22v-6h6v6M9 12h6" stroke="#8CFF2E" strokeWidth="1.5" /></svg></div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-display tracking-tight text-white">Dubai Properties</h1>
            <p className="text-zinc-400 mt-1 text-[15px]">Own a piece of Dubai. Built for high-yield. Backed by OneX.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="properties-saved-btn" className="btn-ghost"><Heart size={14} /> Saved Properties{savedCount ? ` (${savedCount})` : ""}</button>
          <button data-testid="properties-notifications-btn" className="w-11 h-11 rounded-full border border-[#27272A] bg-[#15161A] hover:bg-[#1E1F24] flex items-center justify-center"><Filter size={14} className="text-zinc-300" /></button>
        </div>
      </div>

      {/* Filter row */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`properties-filter-${c.id}`}
              className={`px-5 py-2.5 rounded-full text-[13px] font-medium transition-all border ${category === c.id ? "onex-gold-fill border-[#8CFF2E]" : "border-[#27272A] text-zinc-300 hover:border-[#8CFF2E]/30"}`}
            >{c.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-ghost text-zinc-300" data-testid="properties-sort-btn"><ListOrdered size={14} /> Sort by: Launch Date</button>
          <button className="btn-ghost text-zinc-300" data-testid="properties-filter-btn"><Filter size={14} /> Filter</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-8">
        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {properties.map((p) => (
            <div key={p.id} className="onex-card overflow-hidden hover:-translate-y-1 hover:border-[#8CFF2E]/30 transition-all flex flex-col" data-testid={`property-card-${p.id}`}>
              <div className="relative h-56">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 top-0 p-4 flex items-start justify-between">
                  <span className="onex-gold-fill onex-pill !text-[10px] !px-3">{p.status}</span>
                  <button onClick={() => save(p)} data-testid={`property-save-${p.id}`} className={`w-10 h-10 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${p.saved ? "bg-[#8CFF2E] text-black" : "bg-black/40 text-white hover:bg-black/60"}`}>
                    <Heart size={16} fill={p.saved ? "#0A0A0B" : "none"} />
                  </button>
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-[18px] font-semibold text-white leading-tight">{p.name}</h3>
                <div className="flex items-center gap-2 text-zinc-400 text-[13px] mt-2"><MapPin size={13} className="text-[#8CFF2E]" /> {p.location}</div>
                <p className="text-zinc-400 text-[13px] mt-3 leading-relaxed line-clamp-3">{p.description}</p>
                <div className="grid grid-cols-3 gap-3 mt-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Min. Investment</div>
                    <div className="text-white font-semibold text-[14px] mt-1">AED {p.min_investment.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1">Est. Yield <Info size={10} className="text-zinc-600" /></div>
                    <div className="text-white font-semibold text-[14px] mt-1">{p.yield_low} – {p.yield_high}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Available Spots</div>
                    <div className="text-[#22C55E] font-semibold text-[14px] mt-1">{p.spots_available} / {p.spots_total}</div>
                  </div>
                </div>
                <button
                  onClick={() => join(p)}
                  data-testid={`property-join-${p.id}`}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[14px] font-semibold transition-all ${
                    p.joined_waitlist
                      ? "bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/25"
                      : "btn-gold"
                  }`}
                >
                  {p.joined_waitlist
                    ? <><Check size={14} /> Waitlist Joined · Remind me</>
                    : <>Join Waitlist <ArrowRight size={14} /></>}
                </button>
                <div className="mt-4 flex items-center gap-2 text-zinc-500 text-[12px]">
                  <div className="flex -space-x-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-[#15161A] bg-[#8CFF2E]/20" />
                    ))}
                  </div>
                  <span>{p.waitlist_count}+ on waitlist</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why invest in Dubai sidebar */}
        <div className="onex-card p-6 h-fit" data-testid="why-dubai-card">
          <h3 className="text-[18px] font-semibold text-white">Why Invest in Dubai?</h3>
          <div className="mt-5 space-y-5">
            {[
              { icon: BarChart3, title: "High Rental Demand", body: "Dubai ranks among the top cities for rental yields." },
              { icon: ShieldCheck, title: "100% Ownership", body: "Foreign investors enjoy full ownership rights." },
              { icon: Globe2, title: "Tax Benefits", body: "No property tax and no capital gains tax." },
              { icon: TrendingUp, title: "Strong ROI", body: "Attractive returns with long-term growth." },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#1F3008] border border-[#8CFF2E]/30 flex items-center justify-center shrink-0"><Icon size={16} className="text-[#8CFF2E]" /></div>
                  <div>
                    <div className="text-white text-[14px] font-medium">{b.title}</div>
                    <div className="text-zinc-500 text-[12px] mt-1 leading-relaxed">{b.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="mt-6 text-[13px] text-[#8CFF2E] hover:underline flex items-center gap-1" data-testid="learn-more-dubai-btn">Learn more about Dubai <ArrowRight size={14} /></button>
        </div>
      </div>

      {/* Not a member yet section */}
      <div className="onex-card mt-10 p-8" data-testid="not-member-section">
        <div className="flex items-start justify-between gap-8 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <h3 className="text-[20px] font-semibold text-white">Not a Member Yet?</h3>
            <p className="text-zinc-400 text-[13px] mt-1">Join the waitlist and become a co-owner in just a few simple steps.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 relative">
              {["Join Waitlist", "Verify Mobile", "Complete KYC", "Attend Webinar", "Reserve Interest"].map((s, i) => (
                <div key={s} className="text-center" data-testid={`signup-step-${i + 1}`}>
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#15161A] border border-[#8CFF2E]/30 flex items-center justify-center"><span className="text-[#8CFF2E] text-[13px] font-semibold">{i + 1}</span></div>
                  <div className="text-[12px] text-white mt-2">{s}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="onex-card-soft p-6 max-w-sm">
            <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-2xl bg-[#1F3008] border border-[#8CFF2E]/30 flex items-center justify-center"><Gift size={16} className="text-[#8CFF2E]" /></div><div className="text-white text-[15px] font-medium">Early Access Advantage</div></div>
            <p className="text-zinc-400 text-[13px] mt-3">Be the first to know about new launches and secure the best allocation opportunities.</p>
            <button onClick={() => join(properties[0])} className="mt-5 w-full btn-ghost border-[#8CFF2E]/30 text-[#8CFF2E]" data-testid="not-member-join-btn">Join Waitlist Now <ArrowRight size={14} /></button>
          </div>
        </div>
      </div>

      {waitlistedProperty && (
        <WaitlistJoinedModal
          property={waitlistedProperty}
          onClose={() => setWaitlistedProperty(null)}
        />
      )}
    </div>
  );
};

export default DubaiProperties;
